import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/auth/AuthContext";
import TenantContext from "../../context/tenant/TenantContext";
import useTranslator from "../../hooks/useTranslator";
import constants from "../../Utils/constants";
import { Avatar, Badge, Button, Card, Icon, InfoList, Modal } from "../design";
import styles from "./Occupants.module.css";
import "../TenantDetails/tenantInfoCards.css";
import OccupantForm, {
  buildOccupantsPayload,
  emptyOccupant,
  toOccupantForm,
  validateOccupant,
} from "./OccupantForm";

// Company login tenants: card above Documents on the profile page (tenant and
// landlord). Clicking an occupant opens a read-only popup with their data and
// documents. Tenant only (readOnly false): add / edit in a centred popup and
// delete after a confirmation. Saves the whole Tenant.occupants list through
// the profile update; files of a deleted occupant, or removed from one, are
// deleted from Tenant_docs.
const OccupantsCard = ({ user, readOnly = false }) => {
  const { _t } = useTranslator();
  const { updateProfile } = useContext(AuthContext);
  const { uploadFileData, deleteDocument, fetchTenantDocs } =
    useContext(TenantContext);
  const [docsById, setDocsById] = useState({});
  const [docsLoaded, setDocsLoaded] = useState(false);
  // Popup: null (closed), or { mode: "view" | "edit", index } where index
  // -1 is a new occupant.
  const [popup, setPopup] = useState(null);
  const [form, setForm] = useState(emptyOccupant());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // The list just saved, shown until the refreshed user arrives (the user is
  // reloaded in the background after a save and can take a few seconds).
  const [savedOccupants, setSavedOccupants] = useState(null);
  const occupants = savedOccupants ?? user?.occupants ?? [];

  useEffect(() => {
    setSavedOccupants(null);
  }, [user?.occupants]);

  const loadDocs = () => {
    if (!user?._id) return;
    fetchTenantDocs(user._id).then((res) => {
      const list = res?.data?.success_res?.tenantDocs ?? [];
      setDocsById(
        Object.fromEntries(
          list
            .filter((doc) => doc?.type === constants.OCCUPANT_ID_PROOF)
            .map((doc) => [String(doc._id), doc])
        )
      );
      setDocsLoaded(true);
    });
  };

  useEffect(() => {
    loadDocs();
  }, [user?._id, user?.occupants]);

  const openForm = (index) => {
    if (index === -1) {
      setForm(emptyOccupant());
    } else {
      // Only saved files that still exist are kept in the form.
      const saved = toOccupantForm(occupants[index]);
      setForm({
        ...saved,
        id_proof_docs: saved.id_proof_docs.filter((id) => docsById[id]),
      });
    }
    setErrors({});
    setPopup({ mode: "edit", index });
  };

  // Saves `nextForms` as the full occupants list and deletes `removedDocIds`.
  const saveOccupants = async (nextForms, removedDocIds = []) => {
    setSaving(true);
    try {
      const payload = await buildOccupantsPayload(nextForms, uploadFileData);
      const saved = await updateProfile(user._id, {
        occupants: payload,
        role: "tenant",
      });
      // Failed save: error toast already shown; keep the popup open.
      if (!saved) return;
      for (const docId of removedDocIds) {
        await deleteDocument(docId, user._id);
      }
      setSavedOccupants(payload);
      loadDocs();
      setPopup(null);
    } catch (err) {
      console.error("Error saving occupants:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveForm = async () => {
    const formErrors = validateOccupant(form, _t);
    setErrors(formErrors);
    if (Object.keys(formErrors).length) return;

    const forms = occupants.map(toOccupantForm);
    let removedDocIds = [];
    if (popup.index === -1) {
      forms.push(form);
    } else {
      removedDocIds = forms[popup.index].id_proof_docs.filter(
        (id) => docsById[id] && !form.id_proof_docs.includes(id)
      );
      forms[popup.index] = form;
    }
    await saveOccupants(forms, removedDocIds);
  };

  const handleDelete = async (index) => {
    const forms = occupants.map(toOccupantForm);
    const [removed] = forms.splice(index, 1);
    await saveOccupants(
      forms,
      removed.id_proof_docs.filter((id) => docsById[id])
    );
  };

  // Occupant waiting for the delete confirmation (index), or null.
  const [deleteIndex, setDeleteIndex] = useState(null);
  const confirmDelete = async () => {
    await handleDelete(deleteIndex);
    setDeleteIndex(null);
  };

  const viewed = popup?.mode === "view" ? occupants[popup.index] : null;
  // Saved files of an occupant that still exist in Tenant_docs (a file id
  // with no document behind it is not shown).
  const existingDocs = (occupant) =>
    (occupant?.id_proof_docs || [])
      .map((docRef) => docsById[String(docRef?._id || docRef)])
      .filter(Boolean);
  const documentTypeLabel = (occupant) =>
    occupant?.document_type === "passport"
      ? _t("passport", "Passport")
      : _t("dniNie", "DNI / NIE");
  const stop = (handler) => (e) => {
    e.stopPropagation();
    handler();
  };

  return (
    <>
      <Card
        className="tic-card"
        title={
          <span className="tic-card__title">
            <Icon name="users" size={20} />
            {_t(
              "occupants",
              "Company profile: title of the occupants tile and side modal (people who will live in the property)"
            )}
            <Badge tone="ink">{occupants.length}</Badge>
          </span>
        }
        actions={
          !readOnly && (
            <Button
              variant="secondary"
              size="sm"
              iconStart={<Icon name="plus" size={16} />}
              disabled={saving}
              onClick={() => openForm(-1)}
            >
              {_t(
                "addOccupant",
                "Occupants step: button to add another person who will live in the property"
              )}
            </Button>
          )
        }
      >
        {occupants.length === 0 ? (
          <p className={styles.muted}>
            {_t(
              "noOccupantsYet",
              "Occupants side modal: empty state when the company has not added any occupant yet"
            )}
          </p>
        ) : (
          <div className="tic-income-list">
            {occupants.map((occupant, index) => (
              <div
                key={occupant._id || index}
                className="tic-income-row tic-income-row--click"
                role="button"
                tabIndex={0}
                onClick={() => setPopup({ mode: "view", index })}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setPopup({ mode: "view", index });
                  }
                }}
              >
                <Avatar name={occupant.name} />
                <span className="tic-income-row__info">
                  <span className="tic-income-row__value">
                    <b>{occupant.name}</b>
                  </span>
                  <span className="tic-income-row__meta">
                    {`${documentTypeLabel(occupant)} ${
                      occupant.document_number
                    }${
                      docsLoaded
                        ? ` · ${existingDocs(occupant).length} ${_t(
                            "occupantFilesCount",
                            "Occupants side modal: label after the number of documents of an occupant"
                          )}`
                        : ""
                    }`}
                  </span>
                </span>
                {readOnly && (
                  // The row opens the details too; the button makes that
                  // visible to read-only viewers (landlord).
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={stop(() => setPopup({ mode: "view", index }))}
                  >
                    {_t("viewDetails", "View details")}
                  </Button>
                )}
                {!readOnly && (
                  <span className={styles.fileActions}>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconOnly
                      aria-label={_t("edit", "Edit")}
                      iconStart={<Icon name="edit" size={16} />}
                      disabled={saving}
                      onClick={stop(() => openForm(index))}
                    />
                    <Button
                      variant="dangerGhost"
                      size="sm"
                      iconOnly
                      aria-label={_t(
                        "deleteOccupant",
                        "Occupants side modal: button to delete an occupant and their documents"
                      )}
                      iconStart={<Icon name="trash" size={16} />}
                      disabled={saving}
                      onClick={stop(() => setDeleteIndex(index))}
                    />
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Read-only view of one occupant, with their documents. */}
      <Modal
        open={!!viewed}
        onClose={() => setPopup(null)}
        title={viewed?.name}
        align="start"
        actionsLayout="end"
        closeLabel={_t("close", "Close")}
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setPopup(null)}
            >
              {_t("close", "Close")}
            </Button>
            {!readOnly && (
              <Button
                size="lg"
                iconStart={<Icon name="edit" size={16} />}
                onClick={() => openForm(popup.index)}
              >
                {_t("edit", "Edit")}
              </Button>
            )}
          </>
        }
      >
        {viewed && (
          <div className={styles.fields}>
            <InfoList
              items={[
                {
                  label: _t(
                    "occupantFullName",
                    "Occupants step: label of the occupant's full name field"
                  ),
                  value: viewed.name,
                },
                {
                  label: _t("documentType", "Document type"),
                  value: documentTypeLabel(viewed),
                },
                {
                  label: documentTypeLabel(viewed),
                  value: viewed.document_number,
                },
              ]}
            />
            <p className={styles.sectionLabel}>
              {_t(
                "occupantDocuments",
                "Occupants: label of the documents of one occupant (ID proof or other documents)"
              )}
            </p>
            {docsLoaded && existingDocs(viewed).length === 0 ? (
              <p className={styles.muted}>
                {_t(
                  "occupantNoDocuments",
                  "Occupant view popup: shown when the occupant has no documents"
                )}
              </p>
            ) : (
              existingDocs(viewed).map((doc) => (
                <div key={String(doc._id)} className={styles.savedFile}>
                  <span className={styles.fileName}>
                    <Icon name="fileText" size={16} />
                    {doc.file_name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={_t(
                      "viewDocument",
                      "Occupant view popup: button that opens one of the occupant's documents in a new tab"
                    )}
                    iconStart={<Icon name="eye" size={16} />}
                    disabled={!doc.url}
                    onClick={() =>
                      window.open(doc.url, "_blank", "noopener,noreferrer")
                    }
                  />
                </div>
              ))
            )}
          </div>
        )}
      </Modal>

      {/* Add / edit one occupant. */}
      <Modal
        open={popup?.mode === "edit"}
        onClose={() => !saving && setPopup(null)}
        title={
          popup?.index === -1
            ? _t(
                "addOccupant",
                "Occupants step: button to add another person who will live in the property"
              )
            : _t(
                "editOccupant",
                "Occupant popup: title when editing an existing occupant"
              )
        }
        align="start"
        actionsLayout="end"
        closeLabel={_t("close", "Close")}
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              disabled={saving}
              onClick={() => setPopup(null)}
            >
              {_t("cancel", "Cancel")}
            </Button>
            <Button size="lg" loading={saving} onClick={handleSaveForm}>
              {_t("save", "Save")}
            </Button>
          </>
        }
      >
        <OccupantForm
          index={0}
          occupant={form}
          errors={errors}
          existingDocs={docsById}
          onChange={(changes) => {
            setForm((prev) => ({ ...prev, ...changes }));
            setErrors({});
          }}
        />
      </Modal>

      {/* Delete confirmation (tenant only). */}
      <Modal
        open={deleteIndex !== null}
        onClose={() => !saving && setDeleteIndex(null)}
        size="sm"
        title={_t(
          "deleteOccupantTitle",
          "Occupant delete popup: question asking to confirm deleting an occupant"
        )}
        description={_t(
          "deleteOccupantDescription",
          "Occupant delete popup: explains that the occupant and all their documents will be deleted"
        )}
        closeLabel={_t("close", "Close")}
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              disabled={saving}
              onClick={() => setDeleteIndex(null)}
            >
              {_t("cancel", "Cancel")}
            </Button>
            <Button
              variant="danger"
              size="lg"
              loading={saving}
              onClick={confirmDelete}
            >
              {_t(
                "deleteOccupant",
                "Occupants side modal: button to delete an occupant and their documents"
              )}
            </Button>
          </>
        }
      />
    </>
  );
};

export default OccupantsCard;
